import { BASE_URL, post } from 'utils/sdk';
import { ProductCategory, ProductSubcategory } from './types/product';
import { EPriceType } from 'components/NewProductStepper/ProductBaseForm/constants';
import { flagsToExclusiveNotificationChannel } from 'utils/notificationChannel';

export interface IProductPrice {
  id: number | null;
  label?: string;
  price: number;
  arrangement?: number;
  hour_label?: string;
  time_label?: string;
  timeLabel?: string;
  // We need of two types of time label because this interface is used form new and old sdk
  // and for this reason we need to keep both
  isVariablePrice?: boolean;
  is_variable_price?: boolean;
  // Only on tiers of a local product with multiple prices
  ownerLocalProductAmount?: string;
}
interface IProductCreateProps {
  storeId: number;
  massCreate: boolean;
  name: string;
  category: ProductCategory;
  description: string;
  cancellationPolicy?: string;
  price: number;
  priceType: EPriceType;
  productPrices: Array<IProductPrice>;
  isEarlyCheckin: boolean;
  isLateCheckout: boolean;
  quantity: number;
  hasQuantity: boolean;
  preorderBy: number;
  showDeliveryDate: boolean;
  image: string;
  alertThreshold?: number;
  requiresApproval?: boolean;
  subcategory?: string;
  externalLink?: string;
  instructions?: string;
  productDelegates?: Array<{
    id: number;
    email: string;
    phoneNumber: string;
    isSmsEnabled: boolean;
    isWhatsappEnabled: boolean;
    isSalesSmsEnabled: boolean;
    isRequestSmsEnabled: boolean;
    isShowPrice: boolean;
  }>;
  extraFields?: Array<{
    label: string;
  }>;
  isPurchasableOffPlatform: boolean;
  instructionsOffPlatform?: string;
}

interface IProductUpdateProps {
  productId: number;
  massCreate: boolean;
  massUpdate: boolean;
  name: string;
  category: ProductCategory;
  description: string;
  cancellationPolicy?: string;
  price: number;
  priceType: EPriceType;
  productPrices: Array<IProductPrice>;
  isEarlyCheckin: boolean;
  isLateCheckout: boolean;
  quantity: number;
  hasQuantity: boolean;
  preorderBy: number;
  showDeliveryDate: boolean;
  image: string | undefined;
  alertThreshold?: number;
  requiresApproval?: boolean;
  subcategory?: string;
  externalLink?: string;
  instructions?: string;
  productDelegates?: Array<{
    id: number;
    email: string;
    phoneNumber: string;
    isSmsEnabled: boolean;
    isWhatsappEnabled: boolean;
    isSalesSmsEnabled: boolean;
    isRequestSmsEnabled: boolean;
    isShowPrice: boolean;
  }>;
  extraFields?: Array<{
    label: string;
  }>;
  isPurchasableOffPlatform: boolean;
  instructionsOffPlatform?: string;
  isDefaultPayoutSplit?: boolean;
  amountSplits?: Array<{ recipient?: string | null; amount?: number | string | null }>;
  percentageSplits?: Array<{ recipient: string; percents: number | string }>;
}

export const productCreate = ({
  storeId,
  massCreate,
  name,
  category,
  description,
  cancellationPolicy,
  price,
  priceType,
  productPrices,
  isEarlyCheckin,
  isLateCheckout,
  quantity,
  hasQuantity,
  preorderBy,
  image,
  alertThreshold,
  requiresApproval,
  subcategory,
  externalLink,
  instructions,
  productDelegates,
  extraFields,
  showDeliveryDate,
  isPurchasableOffPlatform,
  instructionsOffPlatform,
}: IProductCreateProps) => {
  const formattedProductDelegates = productDelegates?.map(
    ({
      id,
      email,
      phoneNumber,
      isSmsEnabled,
      isWhatsappEnabled,
      isSalesSmsEnabled,
      isRequestSmsEnabled,
      isShowPrice,
    }) => ({
      id,
      email,
      phone_number: phoneNumber,
      notification_channel: flagsToExclusiveNotificationChannel({
        isSmsEnabled,
        isWhatsappEnabled,
      }),
      is_sales_sms_enabled: isSalesSmsEnabled,
      is_request_sms_enabled: isRequestSmsEnabled,
      is_show_price: isShowPrice,
    }),
  );

  return post<{ product_group_id?: number }>(`${BASE_URL}services/product/create/`, {
    store_id: storeId,
    mass_create: massCreate,
    product_name: name,
    category,
    description,
    cancellation_policy: cancellationPolicy,
    price: priceType === EPriceType.STATIC ? price : 0,
    price_type: priceType,
    product_prices:
      priceType === EPriceType.MULTIPLE
        ? productPrices.map((item, index) => ({
            ...item,
            arrangement: index,
            time_label: item.timeLabel,
            is_variable_price: item.isVariablePrice,
          }))
        : [],
    is_early_checkin: isEarlyCheckin,
    is_late_checkout: isLateCheckout,
    quantity,
    has_quantity: hasQuantity,
    preorder_by: preorderBy,
    image,
    alert_threshold: alertThreshold,
    requires_approval: requiresApproval,
    subcategory,
    external_link: externalLink,
    instructions,
    product_delegates: formattedProductDelegates,
    extra_fields: extraFields,
    show_delivery_date: showDeliveryDate,
    is_purchasable_off_platform: isPurchasableOffPlatform,
    instructions_off_platform: instructionsOffPlatform,
  });
};

export const productUpdate = ({
  productId,
  massCreate,
  massUpdate,
  name,
  category,
  description,
  cancellationPolicy,
  price,
  priceType,
  quantity,
  hasQuantity,
  preorderBy,
  image,
  alertThreshold,
  requiresApproval,
  subcategory,
  externalLink,
  instructions,
  productDelegates,
  extraFields,
  productPrices,
  isEarlyCheckin,
  isLateCheckout,
  showDeliveryDate,
  isPurchasableOffPlatform,
  instructionsOffPlatform,
  isDefaultPayoutSplit,
  amountSplits,
  percentageSplits,
}: IProductUpdateProps) => {
  const formattedProductDelegates = productDelegates?.map(
    ({
      id,
      email,
      phoneNumber,
      isSmsEnabled,
      isWhatsappEnabled,
      isSalesSmsEnabled,
      isRequestSmsEnabled,
      isShowPrice,
    }) => ({
      id,
      email,
      phone_number: phoneNumber,
      notification_channel: flagsToExclusiveNotificationChannel({
        isSmsEnabled,
        isWhatsappEnabled,
      }),
      is_sales_sms_enabled: isSalesSmsEnabled,
      is_request_sms_enabled: isRequestSmsEnabled,
      is_show_price: isShowPrice,
    }),
  );

  // The response is part of Product/ Partial<Product> but I want to be explicit
  return post<{
    affiliate_link: string | null;
    category: ProductCategory;
    description: string;
    external_link: string | null;
    id: number;
    image: string;
    instructions: string | null;
    owner_local_product_amount: string;
    owner_local_product_commission: string;
    price: string;
    price_type: EPriceType;
    product_group_id?: number;
    product_name: string;
    store_id: number;
    subcategory: ProductSubcategory;
    is_purchasable_off_platform: boolean;
    instructions_off_platform: string | null;
    cancellation_policy?: string;
    is_default_payout_split?: boolean;
    wholesale_costs?: Array<{ recipient: string; amount: number | string }>;
    percentage_splits?: Array<{ recipient: string; percents: number | string }>;
  }>(`${BASE_URL}services/product/${productId}/update/`, {
    product_name: name,
    category,
    description,
    cancellation_policy: cancellationPolicy,
    price: priceType === EPriceType.STATIC ? price : 0,
    price_type: priceType,
    product_prices:
      priceType === EPriceType.MULTIPLE
        ? productPrices.map((item, index) => ({
            ...item,
            arrangement: index,
            time_label: item.timeLabel,
            is_variable_price: item.isVariablePrice,
          }))
        : [],
    is_early_checkin: isEarlyCheckin,
    is_late_checkout: isLateCheckout,
    quantity,
    has_quantity: hasQuantity,
    preorder_by: preorderBy,
    image,
    alert_threshold: alertThreshold,
    requires_approval: requiresApproval,
    subcategory,
    external_link: externalLink,
    instructions,
    product_delegates: formattedProductDelegates,
    extra_fields: extraFields,
    show_delivery_date: showDeliveryDate,
    mass_create: massCreate,
    mass_update: massUpdate,
    is_purchasable_off_platform: isPurchasableOffPlatform,
    instructions_off_platform: instructionsOffPlatform,
    is_default_payout_split: isDefaultPayoutSplit,
    wholesale_costs: isDefaultPayoutSplit
      ? []
      : amountSplits
          // The form always renders one empty wholesale row; don't send it.
          ?.filter((item) => item.recipient?.trim() || Number(item.amount) > 0)
          .map((item) => ({
            recipient: item.recipient,
            amount: Number(item.amount) || 0,
          })),
    profit_splits: isDefaultPayoutSplit
      ? []
      : percentageSplits?.map((item) => ({
          recipient: item.recipient,
          percents: Number(item.percents) || 0,
        })),
  });
};

export const productDelete = ({ productId }: { productId: number }) => {
  return post(`${BASE_URL}services/product/${productId}/delete/`);
};

export const productActiveUpdate = ({
  productId,
  isActive,
}: {
  productId: number;
  isActive: boolean;
}) => {
  return post<{ is_active: boolean }>(`${BASE_URL}services/product/${productId}/update-active/`, {
    is_active: isActive,
  });
};

export const massProductDelete = ({ productId }: { productId: number }) =>
  post(`${BASE_URL}services/product/${productId}/mass-delete/`);

export const createProductGroupAndPropagate = ({ productId }: { productId: number }) =>
  post(`${BASE_URL}services/product/${productId}/propagate/`, {});
